import {
  createEntityAdapter,
  createSelector,
  createSlice,
  EntityState
} from "@reduxjs/toolkit";

import logger from "Libs/logger";
import { createAppAsyncThunk } from "Store/createAppAsyncThunk";
import { getNewBillingSystemError } from "Hooks/billingSystem/getNewBillingSystemError";

import {
  getBillingAccountRequest,
  getUserBillingAccountsRequest
} from "Api/billingSystem";

import type { RootState } from "Store/configureStore";
import type {
  BillingAccountMappingWithLinks,
  BillingAccountCreatableFields,
  BillingAccountUpdatableFields
} from "Api/billingSystem/types";
import { CUSTOMER_TYPE } from "Api/billingSystem/types";
import { createBillingAccount } from "Api/billingSystem/accounts/createBillingAccount.request";
import { updateBillingAccountRequest } from "Api/billingSystem/accounts/updateBillingAccount.request";
import { selectCurrentUserUUID } from "Reducers/app/selectors";

const billingAccountMappingAdapter = createEntityAdapter({
  selectId: (account: BillingAccountMappingWithLinks) => account.id
});

const billingAccountAdapter = createEntityAdapter({
  selectId: (account: BillingAccountMappingWithLinks) => account.id
});

// State type
type BillingAccountsState = {
  mappings: EntityState<BillingAccountMappingWithLinks, string> & {
    loading: boolean;
    loaded: boolean;
    error?: string;
  };
  accounts: EntityState<BillingAccountMappingWithLinks, string> & {
    byIdState: Record<string, { loading: boolean; error?: string }>;
  };
  createState: {
    loading: boolean;
    error?: string;
  };
  updateState: {
    loading: boolean;
    error?: string;
  };
};

const initialState: BillingAccountsState = {
  mappings: billingAccountMappingAdapter.getInitialState({
    loading: false,
    loaded: false
  }),
  accounts: billingAccountAdapter.getInitialState({
    byIdState: {}
  }),
  createState: { loading: false },
  updateState: { loading: false }
};

// Thunks
export const createBillingAccountThunk = createAppAsyncThunk(
  "billingSystem/accounts/create",
  async (payload: BillingAccountCreatableFields, { rejectWithValue }) => {
    try {
      return await createBillingAccount(payload);
    } catch (err) {
      logger(err);
      return rejectWithValue({
        error: getNewBillingSystemError(err) ?? "Something went wrong"
      });
    }
  }
);

export const getUserBillingAccounts = createAppAsyncThunk(
  "billingSystem/accounts/getByUser",
  async (_, { getState, rejectWithValue }) => {
    try {
      const userId = selectCurrentUserUUID(getState());
      return await getUserBillingAccountsRequest(userId);
    } catch (err) {
      logger(err);
      return rejectWithValue({
        error: getNewBillingSystemError(err) ?? "Something went wrong"
      });
    }
  },
  {
    condition: (_, { getState }) => {
      const state = getState();
      const { loading, loaded } =
        state.billingAccounts?.mappings ?? initialState.mappings;

      // Don't fetch if already loading or already loaded
      if (loading || loaded) {
        return false;
      }
      return true;
    }
  }
);

export const updateBillingAccountThunk = createAppAsyncThunk(
  "billingSystem/accounts/update",
  async (
    {
      billingAccountId,
      payload
    }: {
      billingAccountId: string;
      payload: BillingAccountUpdatableFields;
    },
    { rejectWithValue }
  ) => {
    try {
      return await updateBillingAccountRequest(billingAccountId, payload);
    } catch (err) {
      logger(err);
      return rejectWithValue({
        error: getNewBillingSystemError(err) ?? "Something went wrong"
      });
    }
  }
);

export const getBillingAccount = createAppAsyncThunk(
  "billingSystem/accounts/getOne",
  async (
    { billingAccountId }: { billingAccountId: string },
    { rejectWithValue }
  ) => {
    try {
      return await getBillingAccountRequest(billingAccountId);
    } catch (err) {
      logger(err);
      return rejectWithValue({
        error: getNewBillingSystemError(err) ?? "Something went wrong"
      });
    }
  }
);

// Slice
const billingAccountsSlice = createSlice({
  name: "billingAccounts",
  initialState,
  reducers: {
    clearBillingAccountCreateError(state) {
      state.createState.error = undefined;
    },
    clearBillingAccountUpdateError(state) {
      state.updateState.error = undefined;
    }
  },
  extraReducers: builder => {
    // Create a new billing account
    builder
      .addCase(createBillingAccountThunk.pending, state => {
        state.createState.loading = true;
        state.createState.error = undefined;
      })
      .addCase(createBillingAccountThunk.fulfilled, (state, action) => {
        state.createState.loading = false;
        billingAccountAdapter.addOne(state.accounts, action.payload);
        // Also add it to the mappings collection, which backs the billing
        // account dropdown list. Without this the new account only shows up
        // after a full page reload re-fetches the list.
        billingAccountMappingAdapter.addOne(state.mappings, action.payload);
      })
      .addCase(createBillingAccountThunk.rejected, (state, action) => {
        state.createState.loading = false;
        state.createState.error = action.payload?.error;
      });

    // Update a billing account
    builder
      .addCase(updateBillingAccountThunk.pending, state => {
        state.updateState.loading = true;
        state.updateState.error = undefined;
      })
      .addCase(updateBillingAccountThunk.fulfilled, (state, action) => {
        state.updateState.loading = false;
        // Both stores hold the account: the mappings collection backs the
        // account dropdown, the accounts collection backs the detail lookups.
        billingAccountMappingAdapter.setOne(state.mappings, action.payload);
        billingAccountAdapter.setOne(state.accounts, action.payload);
      })
      .addCase(updateBillingAccountThunk.rejected, (state, action) => {
        state.updateState.loading = false;
        state.updateState.error = action.payload?.error;
      });

    // Get billing accounts by user
    builder
      .addCase(getUserBillingAccounts.pending, state => {
        state.mappings.loading = true;
        state.mappings.error = undefined;
      })
      .addCase(getUserBillingAccounts.fulfilled, (state, action) => {
        state.mappings.loading = false;
        state.mappings.loaded = true;
        billingAccountMappingAdapter.setAll(
          state.mappings,
          action.payload.items
        );
      })
      .addCase(getUserBillingAccounts.rejected, (state, action) => {
        state.mappings.loading = false;
        state.mappings.error = action.payload?.error;
      });

    // Get single billing account
    builder
      .addCase(getBillingAccount.pending, (state, { meta }) => {
        state.accounts.byIdState[meta.arg.billingAccountId] = {
          loading: true,
          error: undefined
        };
      })
      .addCase(getBillingAccount.fulfilled, (state, action) => {
        const { billingAccountId } = action.meta.arg;
        state.accounts.byIdState[billingAccountId] = {
          loading: false,
          error: undefined
        };
        billingAccountAdapter.setOne(state.accounts, action.payload);
      })
      .addCase(getBillingAccount.rejected, (state, action) => {
        const { billingAccountId } = action.meta.arg;
        state.accounts.byIdState[billingAccountId] = {
          loading: false,
          error: action.payload?.error
        };
      });
  }
});

export default billingAccountsSlice.reducer;

export const {
  clearBillingAccountCreateError,
  clearBillingAccountUpdateError
} = billingAccountsSlice.actions;

// Selectors
const billingAccountsStateSelector = (state: RootState) =>
  state.billingAccounts ?? initialState;

const billingAccountMappingSelectors =
  billingAccountMappingAdapter.getSelectors(
    (state: RootState) =>
      state.billingAccounts?.mappings ?? initialState.mappings
  );

const billingAccountSelectors = billingAccountAdapter.getSelectors(
  (state: RootState) => state.billingAccounts?.accounts ?? initialState.accounts
);

// Accounts by user ID
export const selectUserBillingAccounts =
  billingAccountMappingSelectors.selectAll;

// The user's accounts plus the one currently being viewed when it isn't among
// them. Staff can open a billing account they aren't a member of, and the
// account dropdown still needs to show it as selected.
export const selectUserBillingAccountsWithCurrent = createSelector(
  selectUserBillingAccounts,
  (state: RootState, billingAccountId?: string) =>
    billingAccountId
      ? billingAccountSelectors.selectById(state, billingAccountId)
      : undefined,
  (accounts, current) =>
    !current || accounts.some(account => account.id === current.id)
      ? accounts
      : [...accounts, current]
);

// Whether the user already owns an individual billing account (only one is
// allowed per user).
export const selectUserHasIndividualBillingAccount = createSelector(
  selectUserBillingAccounts,
  accounts =>
    accounts.some(account => account.customer_type === CUSTOMER_TYPE.individual)
);

export const selectUserBillingAccountsLoading = createSelector(
  billingAccountsStateSelector,
  state => state.mappings.loading
);

export const selectUserBillingAccountsLoaded = createSelector(
  billingAccountsStateSelector,
  state => state.mappings.loaded
);

export const selectUserBillingAccountsError = createSelector(
  billingAccountsStateSelector,
  state => state.mappings.error
);

// Single account by ID
export const selectBillingAccountById = billingAccountSelectors.selectById;

export const selectBillingAccountLoadingById = createSelector(
  billingAccountsStateSelector,
  (_: RootState, billingAccountId: string) => billingAccountId,
  (state, billingAccountId) =>
    state.accounts.byIdState[billingAccountId]?.loading ?? false
);

export const selectBillingAccountErrorById = createSelector(
  billingAccountsStateSelector,
  (_: RootState, billingAccountId: string) => billingAccountId,
  (state, billingAccountId) => state.accounts.byIdState[billingAccountId]?.error
);

// Account create
export const selectBillingAccountCreateLoading = createSelector(
  billingAccountsStateSelector,
  state => !!state.createState.loading
);

export const selectBillingAccountCreateError = createSelector(
  billingAccountsStateSelector,
  state => state.createState.error
);

// Account update
export const selectBillingAccountUpdateLoading = createSelector(
  billingAccountsStateSelector,
  state => !!state.updateState.loading
);

export const selectBillingAccountUpdateError = createSelector(
  billingAccountsStateSelector,
  state => state.updateState.error
);
